import { MOTION, RADIUS, SHADOW } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import type { EventLinkState } from "~/components/events/event-origin";
import { useLocale } from "~/i18n";
import type { NotificationItem } from "~/types/notification";

/**
 * One delivered reminder.
 *
 * The lead ("Za hodinu", "Zítra") is derived from `kind` and resolved at render, not
 * computed from the clock: there is no `Intl.RelativeTimeFormat` anywhere in the
 * monorepo, Czech plural rules are a feature of their own, and the ladder only has
 * three rungs — so the honest label is the one the reminder was scheduled as.
 *
 * Tapping through declares `from: "dashboard"`, which is the right way back for
 * something that was opened from a list rather than from the calendar — including
 * when the list itself was opened by a push notification.
 */
export function NotificationCard({
  item,
  onSelect,
}: {
  item: NotificationItem;
  onSelect?: () => void;
}) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();

  const testId = `notifications.item-${item.event_slug}`;

  const body = (
    <Box
      sx={{
        padding: { xs: 2, md: 2.5 },
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
      }}
    >
      {/*
        The dot carries an accessible name rather than a test id: "unread" is
        information, and a screen reader that only announces the title would say the
        same thing for a row the member has already seen.
      */}
      {item.is_unread ? (
        <Box
          role="img"
          aria-label={t("notifications.unread")}
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: "primary.main",
            flexShrink: 0,
            // Optically centred on the lead line rather than on the box.
            marginTop: "6px",
          }}
        />
      ) : null}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          minWidth: 0,
          // Keeps a read row's text on the same left edge as an unread one's.
          marginInlineStart: item.is_unread ? 0 : "20px",
        }}
      >
        <Typography variant="L2" sx={{ color: "primary.main" }}>
          {t(`notifications.lead.${item.kind}`)}
        </Typography>

        <Typography variant="H3" sx={{ color: "text.primary" }}>
          {item.event_title}
        </Typography>

        <Typography variant="T3" sx={{ color: "text.muted" }}>
          <time dateTime={item.event_starts_at}>
            {formatDate(item.event_starts_at, {
              day: "numeric",
              month: "long",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </time>
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: `${RADIUS.lg}px`,
        transition: `box-shadow ${MOTION.transition}, transform ${MOTION.transition}`,
        "&:hover": { boxShadow: SHADOW.card },
      }}
    >
      {/*
        An event that has been deleted or unpublished since the reminder went out
        still belongs in the history — the member was told about it — but there is
        nothing behind the tap, so the row stays legible and stops being a link.
      */}
      {item.event_available ? (
        <CardActionArea
          component={RouterLink}
          to={`/events/${item.event_slug}`}
          state={{ from: "dashboard" } satisfies EventLinkState}
          onClick={onSelect}
          data-testid={testId}
        >
          {body}
        </CardActionArea>
      ) : (
        <CardActionArea disabled data-testid={testId}>
          {body}
        </CardActionArea>
      )}
    </Card>
  );
}
